import * as THREE from 'three';
import { SeededRandom } from '../utils/random.js';
import { CREATURE_BODY_TYPES, CREATURE_BODY_CONFIG, HARMONY } from '../utils/constants.js';
import { assetLoader } from '../utils/AssetLoader.js';
import { planetPalette, creatureColorFromPalette } from '../utils/palette.js';
import { buildCreatureMesh } from './CreatureMeshBuilder.js';

import {
  SECTION_FILL_ORDER,
  SECTION_INSTRUMENTS,
  INSTRUMENT_SECTION_MAP,
  ORCHESTRA_SECTIONS,
  GENRE_SECTION_CONFIG,
} from '../utils/constants_harmony.js';

/**
 * Procedurally generates creatures as Three.js groups.
 * Each creature belongs to a species with an instrument role
 * (percussion, keys, brass, synth-lead, synth-pad).
 * Species spawn in tight clusters so you hear melodies emerge
 * as you approach each group.
 */

const CREATURE_COLORS = [
  0xff6b9d, 0xc44dff, 0x4dc9ff, 0x4dff88, 0xffdb4d,
  0xff4d4d, 0x4dffdb, 0xff884d, 0xb84dff, 0x4dff4d,
];

const EYE_STYLES = ['single', 'double', 'triple', 'compound', 'none'];

/**
 * Body plan ↔ orchestral section — a creature's silhouette telegraphs its
 * musical role: heavy quadrupeds carry the bass, skittering insectoids the
 * percussion, drifting floaters the pads, and so on.
 */
const SECTION_BODY_MAP = {
  bass:       'quadruped',
  percussion: 'insectoid',
  harmony:    'floater',
  melody:     'biped',
  counter:    'serpent',
  rhythm:     'blob',
  atmosphere: 'floater',
};

/** Size range ↔ register — low voices are big, high voices are small. */
const SECTION_SIZE_RANGE = {
  bass:       [1.2, 1.7],
  percussion: [0.7, 1.1],
  harmony:    [1.0, 1.4],
  melody:     [0.7, 1.1],
  counter:    [0.9, 1.3],
  rhythm:     [0.5, 0.9],
  atmosphere: [1.1, 1.5],
};

// Name pools by instrument role — names reflect the instrument's timbre
const NAME_POOLS = {
  'bass-electric': ['Lowline', 'Groovecoil', 'Subhollow'],
  bassoon: ['Reedloom', 'Murkhorn', 'Bogreed'],
  cello: ['Bowroot', 'Cellaroom', 'Sustainbloom'],
  clarinet: ['Reedwhisper', 'Clariflute', 'Murmurreed'],
  contrabass: ['Deepcoil', 'Underbloom', 'Graveline'],
  flute: ['Breezeflute', 'Whistleleaf', 'Sylphnote'],
  'french-horn': ['Roundcall', 'Hornbraid', 'Brasswell'],
  'guitar-acoustic': ['Strumvine', 'Pluckbark', 'Campstring'],
  'guitar-electric': ['Sparkfret', 'Ampwing', 'Distortail'],
  'guitar-nylon': ['Nylonshade', 'Warmpluck', 'Softspan'],
  harmonium: ['Bellreed', 'Sustainpetal', 'Droneroot'],
  harp: ['Arpeglow', 'Harpsheen', 'Stringpetal'],
  organ: ['Pipeborn', 'Chordvent', 'Doorpipe'],
  piano: ['Hammerbloom', 'Keygrove', 'Chordcore'],
  saxophone: ['Saxmist', 'Blueglide', 'Wailfeather'],
  trombone: ['Slidebulk', 'Tromdeep', 'Brassloom'],
  trumpet: ['Brightspike', 'Hornflare', 'Brassbeak'],
  tuba: ['Tuberoot', 'Gutlow', 'Bulkwave'],
  violin: ['Fiddlecrest', 'Stringwisp', 'Screechvine'],
  xylophone: ['Tinkertap', 'Woodchime', 'Plinkleaf'],
  percussion: ['Drumroot', 'Thudspike', 'Rimshaker'],
};

// Generic fallback pool
const GENERIC_NAMES = ['Aurelion', 'Duskling', 'Glimmerbeak', 'Lumenroot', 'Vespera', 'Orphic'];

export class CreatureGenerator {
  /**
   * Generate creatures from a saved song's track data.
   * Each track with notes becomes a species whose instrument role matches
   * the track. The planet's creatures therefore ARE the previous player's
   * composition — same instruments, same species names.
   *
   * @param {number} planetSeed - deterministic seed for visual variety
   * @param {object} songData   - deserialized composition { tracks, bpm, key, scaleName }
   * @returns {object[]} creature data array
   */
  generateCreaturesFromSong(planetSeed, songData, visualHue = null) {
    this._palette = visualHue != null ? planetPalette(visualHue) : null;
    const rng = new SeededRandom(planetSeed + 7777);
    const creatures = [];

    if (!songData?.tracks?.length) {
      return this.generateCreatures(planetSeed, 20, 'orchestral', visualHue);
    }

    // Only create species for tracks that have actual notes placed
    const activeTracks = songData.tracks.filter(
      t => t.notes && t.notes.length > 0
    );

    if (activeTracks.length === 0) {
      return this.generateCreatures(planetSeed, 20, 'orchestral', visualHue);
    }

    const numSpecies = activeTracks.length;

    const speciesTemplates = [];
    for (let s = 0; s < numSpecies; s++) {
      const track = activeTracks[s];
      // Find the matching instrument role definition
      const role = HARMONY.INSTRUMENT_ROLES.find(r => r.id === track.instrumentRole);
      if (!role) continue;

      const template = this._generateSpeciesTemplate(rng, s, numSpecies, role);
      // Preserve the creature name from the song only if it is a custom name
      // and not just the default instrument label.
      let songName = track.name?.trim() || null;
      if (songName) {
        const normalizedTrackName = songName.replace(/[^a-z0-9]/gi, '').toLowerCase();
        const defaultNames = [role.label, role.id]
          .map(n => n.replace(/[^a-z0-9]/gi, '').toLowerCase());
        if (defaultNames.includes(normalizedTrackName)) {
          songName = null;
        }
      }
      template._songName = songName;
      // Literal note grid for this track — the flock performs these exact
      // cells after the radio is repaired (the reveal must be the real song).
      template._songNotes = Array.isArray(track.notes) ? [...track.notes] : [];
      speciesTemplates.push(template);
    }

    // Distribute creatures across species (3–6 per species for a good flock)
    for (let s = 0; s < speciesTemplates.length; s++) {
      const speciesCount = rng.int(HARMONY.MIN_PER_SPECIES, Math.min(HARMONY.MAX_PER_SPECIES, 6));
      for (let i = 0; i < speciesCount; i++) {
        const creature = this._generateCreature(
          rng, creatures.length, planetSeed, speciesTemplates[s], i
        );
        // Override name with the song track's name for the first creature (used as species name)
        if (i === 0 && speciesTemplates[s]._songName) {
          creature.name = speciesTemplates[s]._songName;
        }
        creatures.push(creature);
      }
    }

    return creatures;
  }

  /**
   * Generate creature data for a planet.
   * Creatures are grouped into species; each species shares a
   * body type, colour palette, instrument role, and harmonic slot.
   * Genre influences which sections are present.
   */
  generateCreatures(planetSeed, count, genre = 'orchestral', visualHue = null) {
    this._palette = visualHue != null ? planetPalette(visualHue) : null;
    const rng = new SeededRandom(planetSeed + 7777);
    const creatures = [];

    // Determine species count and allocate creatures to species
    const numSpecies = rng.int(HARMONY.MIN_SPECIES, HARMONY.MAX_SPECIES);

    // ── Genre-aware balanced section assignment ──
    const assignedRoles = this._assignBalancedRoles(rng, numSpecies, genre);

    const speciesTemplates = [];
    for (let s = 0; s < numSpecies; s++) {
      const role = assignedRoles[s];
      speciesTemplates.push(this._generateSpeciesTemplate(rng, s, numSpecies, role));
    }

    // Distribute creatures across species
    let remaining = count;
    for (let s = 0; s < numSpecies && remaining > 0; s++) {
      const isLast = s === numSpecies - 1;
      const speciesCount = isLast
        ? remaining
        : Math.min(remaining, rng.int(HARMONY.MIN_PER_SPECIES, HARMONY.MAX_PER_SPECIES));
      remaining -= speciesCount;

      for (let i = 0; i < speciesCount; i++) {
        const globalIndex = creatures.length;
        creatures.push(this._generateCreature(rng, globalIndex, planetSeed, speciesTemplates[s], i));
      }
    }

    return creatures;
  }

  /**
   * Assign instrument roles so every planet has a balanced band.
   * Genre-aware: breakcore planets get mostly percussion,
   * ambient planets skip drums, jazz fills all sections, etc.
   */
  _assignBalancedRoles(rng, numSpecies, genre = 'orchestral') {
    const roles = HARMONY.INSTRUMENT_ROLES;
    const rolesById = {};
    for (const r of roles) rolesById[r.id] = r;

    const assigned = [];
    const genreCfg = GENRE_SECTION_CONFIG[genre] || GENRE_SECTION_CONFIG.orchestral;
    const fillOrder = genreCfg.fillOrder || SECTION_FILL_ORDER;
    const sectionOmit = new Set(genreCfg.sectionOmit || []);

    // Phase 1: fill from genre-specific order
    for (let i = 0; i < fillOrder.length && assigned.length < numSpecies; i++) {
      const section = fillOrder[i];
      if (sectionOmit.has(section)) continue;
      const instruments = SECTION_INSTRUMENTS[section];
      if (!instruments || instruments.length === 0) continue;
      // Allow duplicates for genres that list a section multiple times (e.g. breakcore → percussion×3)
      const usedIds = new Set(assigned.map(r => r.id));
      const available = instruments.filter(id => !usedIds.has(id));
      const instId = available.length > 0 ? rng.pick(available) : rng.pick(instruments);
      const role = rolesById[instId];
      if (role) assigned.push(role);
    }

    // Phase 2: fill remaining slots with genre-weighted distribution
    const extraWeights = genreCfg.extraWeights || { melody: 2, rhythm: 2, counter: 1, harmony: 1, bass: 1, percussion: 1 };
    while (assigned.length < numSpecies) {
      const section = this._weightedPick(rng, extraWeights);
      if (sectionOmit.has(section)) continue;
      const instruments = SECTION_INSTRUMENTS[section];
      if (!instruments || instruments.length === 0) continue;
      const usedIds = new Set(assigned.map(r => r.id));
      const available = instruments.filter(id => !usedIds.has(id));
      const instId = available.length > 0 ? rng.pick(available) : rng.pick(instruments);
      const role = rolesById[instId];
      if (role) assigned.push(role);
    }

    return assigned;
  }

  /**
   * Weighted random pick from { key: weight } object.
   */
  _weightedPick(rng, weights) {
    const entries = Object.entries(weights);
    const total = entries.reduce((sum, [, w]) => sum + w, 0);
    let roll = rng.next() * total;
    for (const [key, w] of entries) {
      roll -= w;
      if (roll <= 0) return key;
    }
    return entries[entries.length - 1][0];
  }

  /**
   * Create a species template — shared traits for a flock.
   */
  _generateSpeciesTemplate(rng, speciesIndex, totalSpecies, instrumentRole) {
    // Burn one roll to keep the rng sequence (colors, names) stable, then
    // derive the body plan from the instrument's orchestral section so a
    // species' silhouette telegraphs its musical role.
    rng.pick(CREATURE_BODY_TYPES);
    const section = INSTRUMENT_SECTION_MAP[instrumentRole?.id] || 'rhythm';
    const bodyType = SECTION_BODY_MAP[section] || 'blob';
    const sizeRange = SECTION_SIZE_RANGE[section] || [0.6, 1.2];
    // Sample the planet's palette when available so species harmonize with
    // the terrain; fall back to the legacy global list otherwise.
    const color = this._palette
      ? creatureColorFromPalette(this._palette, rng)
      : rng.pick(CREATURE_COLORS);
    const eyeStyle = rng.pick(EYE_STYLES);

    // Musical role — each species gets a base scale degree so they harmonise
    const baseDegree = Math.round((speciesIndex / totalSpecies) * 5);

    // Cluster anchor — a random direction on the sphere where this species spawns
    const anchorTheta = rng.range(0, Math.PI * 2);
    let anchorPhi = rng.range(0.2, Math.PI - 0.2); // avoid poles

    // The player lands at phi≈0 and hearing is distance-limited, so remap the
    // first two species toward the landing site — the planet must sing within
    // earshot on every seed. The raw rng draw is kept (sequence stability);
    // only its value is compressed toward the pole.
    if (speciesIndex === 0) {
      anchorPhi = 0.22 + (anchorPhi / Math.PI) * 0.3;   // ~19-45 units out
    } else if (speciesIndex === 1) {
      anchorPhi = 0.5 + (anchorPhi / Math.PI) * 0.4;    // the next ridge over
    }

    // Choose a name from the pool matching the instrument role so
    // species names reflect their musical sound. Deterministic via `rng`.
    let pool = GENERIC_NAMES;
    const poolKey = instrumentRole?.sampleFolder || instrumentRole?.id;
    if (poolKey && NAME_POOLS[poolKey]) {
      pool = NAME_POOLS[poolKey];
    }
    const chosenName = rng.pick(pool);

    return {
      speciesId: speciesIndex,
      name: chosenName,
      bodyType,
      color,
      eyeStyle,
      baseDegree,
      instrumentRole, // { id, synthType, rhythmDensity, ... }
      waveform: rng.pick(HARMONY.VOICE_WAVEFORMS),
      pattern: rng.pick(HARMONY.VOICE_PATTERNS),
      size: rng.range(sizeRange[0], sizeRange[1]),
      anchorTheta,
      anchorPhi,
    };
  }

  _generateCreature(rng, index, planetSeed, species, memberIndex) {
    const size = species.size * rng.range(0.7, 1.3);
    const limbCount = species.bodyType === 'serpent' ? 0 :
                      species.bodyType === 'biped' ? 2 :
                      species.bodyType === 'quadruped' ? 4 :
                      species.bodyType === 'insectoid' ? rng.int(4, 8) :
                      0;

    const role = species.instrumentRole;

    // Voice characteristics — tied to instrument role
    // Use orchestral section config for octave range so the section determines the register
    const section = INSTRUMENT_SECTION_MAP[role.id] || 'rhythm';
    const sectionCfg = ORCHESTRA_SECTIONS[section] || ORCHESTRA_SECTIONS.rhythm;
    const octOff = rng.int(sectionCfg.octaveRange[0], sectionCfg.octaveRange[1]);
    const voice = {
      waveform: species.waveform,
      scaleDegree: species.baseDegree + rng.int(0, 2),
      octaveOffset: octOff,
      pattern: species.pattern,
      instrumentRole: role.id,      // 'percussion', 'keys', 'brass', 'synth_lead', 'synth_pad'
      interval: rng.range(HARMONY.MIN_CALL_INTERVAL, HARMONY.MAX_CALL_INTERVAL),
      duration: rng.range(role.noteDuration[0], role.noteDuration[1]),
      vibrato: role.id === 'brass' ? rng.range(2, 5) : rng.range(0, 3),
      harmonics: rng.int(1, 3),
      isLeader: memberIndex === 0,
    };

    // Behavior — flocking-aware
    const behavior = {
      speed: rng.range(0.5, 3),
      wanderRadius: rng.range(8, 25),
      flees: rng.next() > 0.7,
      curious: rng.next() > 0.4,
      social: true,
    };

    // Use the species name for the leader (memberIndex === 0) so the
    // species name is consistent in the journal. Other members get an
    // indexed variant.
    const speciesName = species.name || this._generateCreatureName(rng);
    const name = memberIndex === 0 ? speciesName : `${speciesName} ${memberIndex + 1}`;

    // Cluster spawn offset — small random offset from the species anchor
    const spread = HARMONY.CLUSTER_SPREAD;
    const spawnThetaOff = rng.range(-spread, spread);
    const spawnPhiOff = rng.range(-spread, spread);

    return {
      id: `creature_${planetSeed}_${index}`,
      index,
      name,
      songNotes: species._songNotes || null,
      speciesId: species.speciesId,
      bodyType: species.bodyType,
      size,
      color: species.color,
      eyeStyle: species.eyeStyle,
      limbCount,
      voice,
      behavior,
      collected: false,
      velocity: { x: 0, y: 0, z: 0 },
      isLeader: voice.isLeader,
      // Cluster spawn info
      spawnTheta: species.anchorTheta + spawnThetaOff,
      spawnPhi: species.anchorPhi + spawnPhiOff,
    };
  }

  /**
   * Create a 3D mesh for a creature.
   */
  createCreatureMesh(creatureData) {
    const group = new THREE.Group();
    group.userData.creatureData = creatureData;
    const { bodyType, size } = creatureData;

    // ── Model or Procedural Fallback ──
    const config = CREATURE_BODY_CONFIG[bodyType] || {};
    const modelUrl = config.model;

    if (modelUrl) {
      const gltf = assetLoader.cloneModel(modelUrl);
      if (gltf) {
        const model = gltf.scene;
        model.traverse(child => {
          if (child.isMesh) child.castShadow = true;
        });
        const scale = (config.scale || 1.0) * size;
        model.scale.set(scale, scale, scale);
        model.position.y = (config.yOffset || 0) * size;
        group.add(model);

        // ── Animation Setup ──
        if (gltf.animations && gltf.animations.length > 0) {
          const mixer = new THREE.AnimationMixer(model);
          group.userData.mixer = mixer;

          // Play "idle" by default, or the first animation if idle isn't found
          const idleClip = gltf.animations.find(a => a.name.toLowerCase().includes('idle')) || gltf.animations[0];
          if (idleClip) {
            const action = mixer.clipAction(idleClip);
            action.play();
            group.userData.currentAnim = idleClip.name.toLowerCase().includes('idle') ? 'idle' : idleClip.name;
          }
          
          // Store animations for later use (switching to "walk", etc.)
          group.userData.animations = gltf.animations;
        }
        
        group.name = `creature-${creatureData.id}`;
        return group;
      }
    }

    // ── Spore-style procedural creature ──
    const built = buildCreatureMesh(creatureData);
    group.add(built);
    group.userData.anim = built.userData.anim;

    group.name = `creature-${creatureData.id}`;
    return group;
  }

  _generateCreatureName(rng) {
    const adj = rng.pick(['Luminous', 'Speckled', 'Greater', 'Lesser', 'Spotted', 'Striped', 'Glowing', 'Whispering', 'Humming', 'Crystal', 'Shadow', 'Drift', 'Ember', 'Frost', 'Velvet']);
    const syllables = ['morp', 'zyx', 'blip', 'quor', 'thal', 'nex', 'glib', 'vorn', 'plix', 'skar', 'wub', 'Fae', 'dro', 'kip', 'yem'];
    const name = rng.pick(syllables) + rng.pick(syllables);
    return `${adj} ${name.charAt(0).toUpperCase() + name.slice(1)}`;
  }
}
